<template>
  <label
    class="base-radio"
    :class="{
      disabled: disabled,
      checked: isChecked,
      [$attrs.class as string]: true,
    }"
  >
    <input
      ref="radio"
      type="radio"
      :name="name"
      :value="modelValue"
      :disabled="disabled"
      :checked="isChecked"
      v-bind="filteredAttrs"
      @change="onChange"
    />
    <span class="radiobutton" />
    <slot />
  </label>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

@Component({
  inheritAttrs: false,
  emits: ['update:modelValue'],
})
class BaseRadio extends Vue {
  @Prop()
  modelValue!: string

  @Prop()
  name!: string

  @Prop()
  disabled!: boolean

  @Prop()
  value!: string

  get isChecked() {
    // this.$attrs is a proxy object, so we can't use hasOwnProperty
    return 'checked' in this.$attrs
      ? !!this.$attrs.checked
      : this.value === this.modelValue
  }

  get filteredAttrs() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onInput, ...attrs } = this.$attrs
    return attrs
  }

  onChange() {
    this.$emit('update:modelValue', this.value)
  }
}

export default toNative(BaseRadio)
</script>

<style scoped>
label {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
}

label ~ label {
  margin-top: 0.5em;
}

input {
  position: absolute;
  opacity: 0;
  z-index: -1;
}

.radiobutton {
  align-items: center;
  background-color: #eee;
  border-radius: 50%;
  border: 2px solid #bbb;
  display: flex;
  flex-shrink: 0;
  height: 1.4em;
  justify-content: center;
  margin-right: 0.5em;
  position: relative;
  width: 1.4em;
}

.sml .radiobutton {
  font-size: 0.75em;
}

.radiobutton:after {
  content: '';
  position: absolute;
  display: none;
  width: 0.6em;
  height: 0.6em;
  border-radius: 50%;
  background: white;
}

input:checked ~ .radiobutton:after {
  display: block;
}

input:checked ~ .radiobutton {
  background-color: var(--colour-header-dark-blue);
  border-color: var(--colour-header-dark-blue);
}

@media (hover: hover) {
  label:hover input ~ .radiobutton {
    background-color: var(--colour-page-background);
    border: 3px solid var(--colour-header-dark-blue);
  }

  label:hover input:not([disabled]):checked ~ .radiobutton {
    background-color: var(--colour-dark-blue);
    border-color: var(--colour-dark-blue);
  }
}

/* disabled state */
label.disabled {
  color: #bbb;
  pointer-events: none;
  cursor: not-allowed;
}

input:disabled ~ .radiobutton {
  background-color: #eee;
  border-color: #ddd;
  cursor: not-allowed;
}

input:disabled ~ .radiobutton:after {
  border-color: var(--colour-table-borders);
  background: #ddd;
}
</style>
